import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getInvite, acceptInvite, type InvitePublic } from '../api/invites';
import { login } from '../api/user';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  Stack,
  Card,
  CardBody,
  H2,
  Text,
  Input,
  Button,
  FormField,
} from '@neuro/design-system';
import { LoadingFallback } from '../components/LoadingFallback';

export default function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [invite, setInvite] = useState<InvitePublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('No invite token provided');
      setLoading(false);
      return;
    }

    getInvite(token)
      .then((data) => {
        setInvite(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || 'Invalid or expired invite link');
        setLoading(false);
      });
  }, [token]);

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);
    try {
      const result = await acceptInvite(token!, password);
      await login(result.username, password);
      navigate('/');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to accept invite');
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingFallback message="Loading invitation..." />;
  }

  if (error && !invite) {
    return (
      <Stack
        align="center"
        justify="center"
        style={{ minHeight: '100vh', padding: isMobile ? 'var(--nc-space-4, 1rem)' : 0 }}
      >
        <Card padding="lg" style={{ maxWidth: 400, width: '100%' }}>
          <CardBody>
            <H2 className="nc-text--center">Invalid Invite</H2>
            <Stack gap={16} style={{ marginTop: 'var(--nc-space-6, 1.5rem)' }}>
              <Text color="error">{error}</Text>
              <Button variant="secondary" fullWidth onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </Stack>
          </CardBody>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack
      align="center"
      justify="center"
      style={{ minHeight: '100vh', padding: isMobile ? 'var(--nc-space-4, 1rem)' : 0 }}
    >
      <Card padding="lg" style={{ maxWidth: 400, width: '100%' }}>
        <CardBody>
          <H2 className="nc-text--center">Accept Invitation</H2>
          <Text color="secondary" size="sm" className="nc-text--center" style={{ marginTop: 8 }}>
            You've been invited to join as <strong>{invite?.targetUsername}</strong>
          </Text>
          <form onSubmit={handleAccept}>
            <Stack gap={16} style={{ marginTop: 'var(--nc-space-6, 1.5rem)' }}>
              <FormField label="Create Password" required>
                <Input
                  type="password"
                  placeholder="Enter a secure password"
                  value={password}
                  autoFocus
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                  fullWidth
                />
              </FormField>
              <FormField label="Confirm Password" required>
                <Input
                  type="password"
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                  fullWidth
                />
              </FormField>
              <Button
                type="submit"
                variant="primary"
                fullWidth
                loading={submitting}
                disabled={!password || !confirmPassword}
              >
                Create Account
              </Button>
              {error && (
                <Text color="error" size="sm">{error}</Text>
              )}
            </Stack>
          </form>
        </CardBody>
      </Card>
    </Stack>
  );
}
